import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Wallet, Fuel, Ban, Paperclip, X, ArrowDownLeft, ArrowUpRight, Printer, ListTree, Send, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { openPdf } from "@/lib/print";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import BankAccountSelect from "@/components/BankAccountSelect";

import { fmtDate } from "@/lib/format";
import { confirmAction } from "@/lib/dialogs";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const NO_ACTIVITY = "__none__";
const activityLabel = (activities, id) => {
  if (!id) return "—";
  const a = activities.find((w) => w.id === id);
  return a ? `${a.code} — ${a.name}` : "—";
};

function ActivitySelect({ value, onChange, activities }) {
  return (
    <Select value={value || NO_ACTIVITY} onValueChange={(v) => onChange(v === NO_ACTIVITY ? "" : v)}>
      <SelectTrigger className="mt-1.5" data-testid="site-expense-activity"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_ACTIVITY}>No activity (project cost only)</SelectItem>
        {activities.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function AssignActivityDialog({ expense, projectId, activities, onClose, onDone }) {
  const [wbs, setWbs] = useState("");
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(!!expense), () => { setWbs(""); });      // closed without saving: opens fresh next time
  useEffect(() => { setWbs(expense?.wbs_item_id || ""); }, [expense]);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/site-expenses/${expense.id}/activity`, { wbs_item_id: wbs || null });
      toast.success(wbs ? "Expense assigned to the activity" : "Activity removed from the expense");
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!expense} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Assign to an activity</DialogTitle></DialogHeader>
        {expense && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{expense.description} — <span className="font-mono">{inr(expense.total)}</span></p>
            <div><Label>Activity</Label><ActivitySelect value={wbs} onChange={setWbs} activities={activities} /></div>
            <p className="text-[11px] text-muted-foreground">Only the activity this cost counts against in Budget vs Actual changes. The expense and the accounts are not touched.</p>
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="assign-activity-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MONEY_FIELDS = ["expense_date", "category", "amount", "gst_amount", "paid_from", "bank_account_id"];     // changing these reverses the entry and records a corrected one
const sameValue = (a, b) => (typeof a === "number" || typeof b === "number" || /^[0-9.]+$/.test(String(a ?? "")) && /^[0-9.]+$/.test(String(b ?? ""))
  ? Number(a || 0) === Number(b || 0) : String(a ?? "") === String(b ?? ""));

/** Correct a saved expense. Words and the activity change in place; money, date, category or who paid reverses the entry and records a corrected one. */
export function EditExpenseDialog({ expense, projectId, companyId, categories, activities, balance, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!expense) return;
    setForm({ expense_date: expense.expense_date, category: expense.category, description: expense.description, paid_to: expense.paid_to || "", bill_no: expense.bill_no || "",
      amount: expense.amount, gst_amount: expense.gst_amount, paid_from: expense.paid_from, wbs_item_id: expense.wbs_item_id || "", bank_account_id: expense.bank_account_id || "" });
  }, [expense]);
  if (!expense || !form) return <Dialog open={false} onOpenChange={() => {}}><DialogContent /></Dialog>;

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const original = { ...form, ...{ expense_date: expense.expense_date, category: expense.category, description: expense.description, paid_to: expense.paid_to || "", bill_no: expense.bill_no || "",
    amount: expense.amount, gst_amount: expense.gst_amount, paid_from: expense.paid_from, wbs_item_id: expense.wbs_item_id || "", bank_account_id: expense.bank_account_id || "" } };
  const changed = Object.keys(original).filter((k) => !sameValue(original[k], form[k]) && !(k === "bank_account_id" && form.paid_from !== "BANK"));
  const moneyChanged = changed.some((k) => MONEY_FIELDS.includes(k));
  const total = Number(form.amount || 0) + Number(form.gst_amount || 0);
  const available = Number(balance || 0) + (expense.paid_from === "PETTY_CASH" ? Number(expense.total || 0) : 0);       // this entry's own money is given back first
  const tooMuch = form.paid_from === "PETTY_CASH" && total > available;

  const save = async (e) => {
    e.preventDefault();
    if (changed.length === 0) { toast.info("Nothing was changed"); return; }
    setSaving(true);
    try {
      const body = {};
      changed.forEach((k) => { body[k] = ["paid_to", "bill_no", "wbs_item_id", "bank_account_id"].includes(k) ? (form[k] || null) : form[k]; });
      const { data } = await api.patch(`/projects/${projectId}/site-expenses/${expense.id}`, body);
      toast.success(data.replaced ? "Expense corrected. The original was reversed and a corrected one recorded." : "Expense updated");
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Edit site expense</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5" data-testid="edit-site-expense-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" value={form.expense_date} onChange={set("expense_date")} className="mt-1.5" data-testid="edit-expense-date" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="edit-expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Input required value={form.description} onChange={set("description")} className="mt-1.5" data-testid="edit-expense-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Paid to</Label><Input value={form.paid_to} onChange={set("paid_to")} className="mt-1.5" data-testid="edit-expense-paid-to" /></div>
            <div><Label>Bill no.</Label><Input value={form.bill_no} onChange={set("bill_no")} className="mt-1.5" data-testid="edit-expense-bill-no" /></div>
            <div><Label>Amount before GST (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="edit-expense-amount" /></div>
            <div><Label>GST on the bill (₹)</Label><Input type="number" step="0.01" min="0" value={form.gst_amount} onChange={set("gst_amount")} className="mt-1.5" data-testid="edit-expense-gst" /></div>
          </div>
          <div>
            <Label>Paid from</Label>
            <Select value={form.paid_from} onValueChange={(v) => setForm({ ...form, paid_from: v })}>
              <SelectTrigger className="mt-1.5" data-testid="edit-expense-paid-from"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PETTY_CASH">Site petty cash (balance {inr(available)} for this entry)</SelectItem>
                <SelectItem value="BANK">Company bank / UPI</SelectItem>
              </SelectContent>
            </Select>
            {tooMuch && <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">More than the petty cash balance. Ask for a top-up, or choose Company bank.</p>}
          </div>
          {form.paid_from === "BANK" && <BankAccountSelect companyId={companyId} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label="Which bank / cash account" testId="edit-expense-bank-select" />}
          <div><Label>Activity (optional)</Label><ActivitySelect value={form.wbs_item_id} onChange={(v) => setForm({ ...form, wbs_item_id: v })} activities={activities} /></div>
          {moneyChanged && (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300" data-testid="edit-expense-correction-note">
              You changed the amount, GST, date, category or who paid. The original entry will be reversed and a corrected one recorded, so the accounts and the project cost stay right. Both stay in the list; the original shows as cancelled.
            </p>
          )}
          <DialogFooter><Button type="submit" disabled={saving || tooMuch || changed.length === 0} data-testid="edit-expense-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

async function openAttachment(path) {
  const tab = window.open("", "_blank");
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (tab) tab.location.href = url; else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (tab) tab.close();
    toast.error("Could not open the bill photo");
  }
}

export default function SiteExpenses() {
  const { projectId } = useProjectCtx();
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [editing, setEditing] = useState(null);                 // the expense being corrected
  const [tab, setTab] = useState("expenses");
  const [loading, setLoading] = useState(false);

  const [paying, setPaying] = useState(null);          // the cash request a manager is paying (opens the top-up form filled in)
  const [requests, setRequests] = useState([]);
  const loadRequests = async () => { try { const { data: r } = await api.get(`/projects/${projectId}/cash-requests`); setRequests(r); } catch { /* the page works without it */ } };
  const load = async () => {
    loadRequests();
    setLoading(true);
    try {
      const [{ data: res }, wbs] = await Promise.all([
        api.get(`/projects/${projectId}/site-expenses`),
        api.get(`/projects/${projectId}/wbs`, { params: { include_groups: false } }).then((r) => r.data).catch(() => []),
      ]);
      setData(res);
      setActivities(wbs);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (projectId) load(); }, [projectId]);

  const cancelExpense = async (e) => {
    const reason = window.prompt(`Cancel "${e.description}" (${inr(e.total)})? Give a reason:`);
    if (!reason) return;
    try {
      await api.post(`/projects/${projectId}/site-expenses/${e.id}/cancel`, { reason });
      toast.success("Expense cancelled — cost and accounts reversed");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const cancelPetty = async (p) => {
    if (!await confirmAction(`Cancel this petty cash ${p.kind === "TOPUP" ? "top-up" : "return"} of ${inr(p.amount)}?`)) return;
    try {
      await api.post(`/projects/${projectId}/petty-cash/${p.id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const cancelFuel = async (f) => {
    if (!await confirmAction(`Cancel this Fuel Account ${f.kind === "TOPUP" ? "top-up" : "return"} of ${inr(f.amount)}?`)) return;
    try {
      await api.post(`/projects/${projectId}/fuel-account/${f.id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground">{loading ? "Loading…" : ""}</p>;
  const balance = Number(data.petty_balance);
  const fuelBalance = Number(data.fuel_balance);
  const waitingFor = (account) => requests.some((r) => r.account === account && r.status === "REQUESTED");

  return (
    <div className="space-y-5" data-testid="site-expenses-page">
      <PageHeader
        title="Site Expenses"
        description="Diesel, food, transport, repairs, daily wages and other small spends at this site. One entry updates the project cost and the company accounts together."
        action={<div className="flex flex-wrap gap-2">
          {data.can_manage && <PettyCashDialog projectId={projectId} companyId={data.company_id} balance={balance} onSaved={load} payRequest={paying?.account === "PETTY_CASH" ? paying : null} onPayClose={() => setPaying(null)} />}
          {data.can_manage && <FuelAccountDialog projectId={projectId} companyId={data.company_id} balance={fuelBalance} onSaved={load} payRequest={paying?.account === "FUEL_ACCOUNT" ? paying : null} onPayClose={() => setPaying(null)} />}
          {data.can_enter && <ExpenseDialog projectId={projectId} companyId={data.company_id} categories={data.categories} activities={activities} balance={balance} onSaved={load} />}
        </div>}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Petty cash at site</p>
          <p className={`mt-2 font-mono text-lg font-bold ${balance < 0 ? "text-rose-500" : balance < 2000 ? "text-amber-600 dark:text-amber-400" : "text-foreground"}`}>{inr(balance)}</p>
          {(balance < 2000 || data.petty_low) && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Running low</p>}
          {data.can_enter && <RequestCashDialog projectId={projectId} account="PETTY_CASH" label="petty cash" balance={balance} low={balance < 2000 || data.petty_low} disabledReason={waitingFor("PETTY_CASH") ? "A request for petty cash is already waiting" : ""} onRequested={loadRequests} />}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fuel Account at site</p>
          <p className={`mt-2 font-mono text-lg font-bold ${fuelBalance < 0 ? "text-rose-500" : fuelBalance < 2000 ? "text-amber-600 dark:text-amber-400" : "text-foreground"}`}>{inr(fuelBalance)}</p>
          {(fuelBalance < 2000 || data.fuel_low) && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Running low</p>}
          {data.can_enter && <RequestCashDialog projectId={projectId} account="FUEL_ACCOUNT" label="Fuel Account money" balance={fuelBalance} low={fuelBalance < 2000 || data.fuel_low} disabledReason={waitingFor("FUEL_ACCOUNT") ? "A request for the Fuel Account is already waiting" : ""} onRequested={loadRequests} />}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Spent this month</p>
          <p className="mt-2 font-mono text-lg font-bold text-foreground">{inr(data.spent_this_month)}</p>
        </div>
        <div className="col-span-2 rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">By category (total {inr(data.spent_total)})</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {data.by_category.length === 0 ? <span className="text-muted-foreground">No expenses yet</span> : data.by_category.map((c) => (
              <span key={c.category}><span className="text-muted-foreground">{c.label}</span> <span className="font-mono text-foreground">{inr(c.amount)}</span></span>
            ))}
          </div>
        </div>
      </div>

      <CashRequestsPanel projectId={projectId} rows={requests} canManage={data.can_manage} onPay={setPaying} onChanged={() => { loadRequests(); load(); }} />

      <div className="flex gap-2">
        <Button size="sm" variant={tab === "expenses" ? "default" : "outline"} onClick={() => setTab("expenses")}>Expenses ({data.expenses.length})</Button>
        <Button size="sm" variant={tab === "petty" ? "default" : "outline"} onClick={() => setTab("petty")}>Petty cash given / returned ({data.petty_cash.length})</Button>
        <Button size="sm" variant={tab === "fuel" ? "default" : "outline"} onClick={() => setTab("fuel")}>Fuel Account given / returned ({data.fuel_account.length})</Button>
      </div>

      {tab === "expenses" ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead><TableHead>Paid to / bill</TableHead><TableHead>Activity</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Paid from</TableHead><TableHead>By</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.expenses.length === 0 && <TableRow><TableCell colSpan={9} className="py-6 text-center text-sm text-muted-foreground">No site expenses yet.</TableCell></TableRow>}
              {data.expenses.map((e) => (
                <TableRow key={e.id} className={e.status === "CANCELLED" ? "opacity-50" : ""} data-testid={`site-expense-row-${e.id}`}>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(e.expense_date)}</TableCell>
                  <TableCell className="text-xs">{e.category_label}</TableCell>
                  <TableCell className="max-w-xs text-sm text-foreground">{e.description}{e.status === "CANCELLED" && <div className="text-[11px] text-rose-500">Cancelled: {e.cancel_reason}</div>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.paid_to || "—"}{e.bill_no ? <div>Bill {e.bill_no}</div> : null}</TableCell>
                  <TableCell className="max-w-[180px] text-xs text-muted-foreground" data-testid={`site-expense-activity-${e.id}`}>{activityLabel(activities, e.wbs_item_id)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    <div className="font-semibold text-foreground">{inr(e.total)}</div>
                    {Number(e.gst_amount) > 0 && <div className="text-[10px] text-muted-foreground">incl. GST {inr(e.gst_amount)}</div>}
                  </TableCell>
                  <TableCell className="text-xs">{e.paid_from === "PETTY_CASH" ? "Petty cash" : (e.bank_account || "Bank")}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.entered_by || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button size="sm" variant="ghost" title="Print / PDF" onClick={() => openPdf(`/print/site-expenses/${e.id}`)} data-testid={`print-site-expense-${e.id}`}><Printer className="h-3.5 w-3.5" /></Button>
                    {e.has_attachment && <Button size="sm" variant="ghost" title="Bill photo" onClick={() => openAttachment(`/projects/${projectId}/site-expenses/${e.id}/attachment`)}><Paperclip className="h-3.5 w-3.5" /></Button>}
                    {e.can_edit && <Button size="sm" variant="ghost" title="Edit" onClick={() => setEditing(e)} data-testid={`edit-site-expense-${e.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                    {e.status === "POSTED" && data.can_enter && <Button size="sm" variant="ghost" title="Assign to an activity" onClick={() => setAssigning(e)} data-testid={`assign-activity-${e.id}`}><ListTree className="h-3.5 w-3.5" /></Button>}
                    {e.status === "POSTED" && data.can_enter && <Button size="sm" variant="ghost" title="Cancel" onClick={() => cancelExpense(e)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : tab === "petty" ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Holder</TableHead><TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>By</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.petty_cash.length === 0 && <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No petty cash given to this site yet.</TableCell></TableRow>}
              {data.petty_cash.map((p) => (
                <TableRow key={p.id} className={p.status === "CANCELLED" ? "opacity-50" : ""}>
                  <TableCell className="text-xs">{fmtDate(p.entry_date)}</TableCell>
                  <TableCell className="text-xs">{p.kind === "TOPUP" ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><ArrowDownLeft className="h-3.5 w-3.5" /> Given to site</span> : <span className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400"><ArrowUpRight className="h-3.5 w-3.5" /> Returned</span>}{p.status === "CANCELLED" && <span className="ml-1 text-rose-500">(cancelled)</span>}</TableCell>
                  <TableCell className="text-sm">{p.holder || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.reference || "—"}{p.bank_account ? <div>{p.kind === "TOPUP" ? "From" : "To"}: {p.bank_account}</div> : null}{p.remarks ? <div>{p.remarks}</div> : null}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{inr(p.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.entered_by || "—"}</TableCell>
                  <TableCell className="text-right">{p.status === "POSTED" && data.can_manage && <Button size="sm" variant="ghost" onClick={() => cancelPetty(p)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Fuel station</TableHead><TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>By</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.fuel_account.length === 0 && <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No Fuel Account top-ups yet.</TableCell></TableRow>}
              {data.fuel_account.map((f) => (
                <TableRow key={f.id} className={f.status === "CANCELLED" ? "opacity-50" : ""} data-testid={`fuel-account-row-${f.id}`}>
                  <TableCell className="text-xs">{fmtDate(f.entry_date)}</TableCell>
                  <TableCell className="text-xs">{f.kind === "TOPUP" ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><ArrowDownLeft className="h-3.5 w-3.5" /> Topped up</span> : <span className="inline-flex items-center gap-1 text-sky-600 dark:text-sky-400"><ArrowUpRight className="h-3.5 w-3.5" /> Returned</span>}{f.status === "CANCELLED" && <span className="ml-1 text-rose-500">(cancelled)</span>}</TableCell>
                  <TableCell className="text-sm">{f.fuel_station || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{f.reference || "—"}{f.bank_account ? <div>{f.kind === "TOPUP" ? "From" : "To"}: {f.bank_account}</div> : null}{f.remarks ? <div>{f.remarks}</div> : null}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{inr(f.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{f.entered_by || "—"}</TableCell>
                  <TableCell className="text-right">{f.status === "POSTED" && data.can_manage && <Button size="sm" variant="ghost" onClick={() => cancelFuel(f)}><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <AssignActivityDialog expense={assigning} projectId={projectId} activities={activities} onClose={() => setAssigning(null)} onDone={() => { setAssigning(null); load(); }} />
      {editing && <EditExpenseDialog expense={editing} projectId={projectId} companyId={data.company_id} categories={data.categories} activities={activities} balance={balance}
        onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ExpenseDialog({ projectId, companyId, categories, activities, balance, onSaved }) {
  const EMPTY = { expense_date: today(), category: "DIESEL", description: "", paid_to: "", bill_no: "", amount: "", gst_amount: "0", paid_from: "PETTY_CASH", wbs_item_id: "", bank_account_id: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [billFile, setBillFile] = useState(null);
  useResetOnClose(Boolean(open), () => { setForm(EMPTY); setBillFile(null); });      // closed without saving: opens fresh next time
  const fileRef = useRef(null);
  const removeBill = () => { setBillFile(null); if (fileRef.current) fileRef.current.value = ""; };
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const total = Number(form.amount || 0) + Number(form.gst_amount || 0);
  const tooMuch = form.paid_from === "PETTY_CASH" && total > balance;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/site-expenses`, {
        ...form, paid_to: form.paid_to || null, bill_no: form.bill_no || null, gst_amount: form.gst_amount || "0", wbs_item_id: form.wbs_item_id || null, bank_account_id: form.paid_from === "BANK" ? form.bank_account_id || null : null,
      });
      const file = billFile;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try { await api.post(`/projects/${projectId}/site-expenses/${data.id}/attachment`, fd); }
        catch (err) { toast.warning(`Saved, but the bill photo failed: ${getErrorMessage(err)}`); }
      }
      toast.success("Expense recorded — project cost and accounts updated");
      setForm(EMPTY);
      removeBill();
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-site-expense-button"><Plus className="h-4 w-4" /> Add expense</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New site expense</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5" data-testid="site-expense-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" value={form.expense_date} onChange={set("expense_date")} className="mt-1.5" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="site-expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Input required value={form.description} onChange={set("description")} placeholder="e.g. Diesel 100 L for JCB & crane" className="mt-1.5" data-testid="site-expense-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Paid to</Label><Input value={form.paid_to} onChange={set("paid_to")} placeholder="Indian Oil pump, Arakalgudu" className="mt-1.5" /></div>
            <div><Label>Bill no.</Label><Input value={form.bill_no} onChange={set("bill_no")} className="mt-1.5" /></div>
            <div><Label>Amount before GST (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="site-expense-amount" /></div>
            <div><Label>GST on the bill (₹)</Label><Input type="number" step="0.01" min="0" value={form.gst_amount} onChange={set("gst_amount")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Paid from</Label>
            <Select value={form.paid_from} onValueChange={(v) => setForm({ ...form, paid_from: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PETTY_CASH">Site petty cash (balance {inr(balance)})</SelectItem>
                <SelectItem value="BANK">Company bank / UPI</SelectItem>
              </SelectContent>
            </Select>
            {tooMuch && <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">More than the petty cash balance — ask for a top-up, or choose Company bank.</p>}
          </div>
          {form.paid_from === "BANK" && <BankAccountSelect companyId={companyId} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label="Which bank / cash account" testId="site-expense-bank-select" />}
          <div><Label>Activity (optional)</Label><ActivitySelect value={form.wbs_item_id} onChange={(v) => setForm({ ...form, wbs_item_id: v })} activities={activities} /></div>
          <div><Label htmlFor="bill-photo">Bill photo (optional)</Label>
            {billFile ? (
              <div className="mt-1.5 flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm" data-testid="bill-photo-chosen">
                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate" title={billFile.name} data-testid="bill-photo-name">{billFile.name}</span>
                <Button type="button" size="sm" variant="ghost" onClick={removeBill} data-testid="bill-photo-remove"><X className="h-3.5 w-3.5" /> Remove</Button>
              </div>
            ) : (
              <Input id="bill-photo" ref={fileRef} type="file" accept="image/*,.pdf" className="mt-1.5" data-testid="bill-photo-input"
                onChange={(e) => setBillFile(e.target.files?.[0] || null)} />
            )}</div>
          <div className="rounded-lg bg-muted/50 p-2.5 text-sm"><div className="flex justify-between font-semibold"><span>Total paid</span><span className="font-mono">{inr(total)}</span></div></div>
          <DialogFooter><Button type="submit" disabled={saving || tooMuch}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save expense</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The site asks the project manager, admins and Accounts for more cash. The request is kept, and shows below until it is paid, refused or withdrawn. */
function RequestCashDialog({ projectId, account, label, balance, low, disabledReason, onRequested }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setAmount(""); setReason(""); });      // closed without saving: opens fresh next time
  const send = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/cash-requests`, { account, amount: Number(amount), reason: reason.trim() || null });
      toast.success("Request sent. You will be told when it is paid or refused.");
      setOpen(false); setAmount(""); setReason("");
      onRequested?.();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant={low ? "default" : "ghost"} disabled={!!disabledReason} title={disabledReason || ""} className="mt-2 h-7 px-2 text-xs" data-testid={`request-${account}-button`}><Send className="h-3 w-3" /> {disabledReason ? "Request waiting" : "Ask for more"}</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`request-${account}-dialog`}>
        <DialogHeader>
          <DialogTitle>Ask for {label}</DialogTitle>
          <DialogDescription>Balance now {inr(balance)}. The project manager, the admins and Accounts are told, and the request stays on this page until it is paid or refused.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Amount needed (₹)</Label><Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" data-testid={`request-${account}-amount`} /></div>
          <div><Label>What it is for (optional)</Label><Textarea rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" placeholder="What it is for, how soon" data-testid={`request-${account}-note`} /></div>
        </div>
        <DialogFooter><Button onClick={send} disabled={saving || !(Number(amount) > 0)} data-testid={`request-${account}-send`}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Send request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REQUEST_TONE = { REQUESTED: "bg-amber-100 text-amber-800", PAID: "bg-emerald-100 text-emerald-800", REJECTED: "bg-rose-100 text-rose-800", CANCELLED: "bg-slate-100 text-slate-600" };

/** The site's cash requests: who asked, how much, and what became of it. A manager pays (opens the top-up form filled in) or refuses; the asker can withdraw. */
function CashRequestsPanel({ projectId, rows, canManage, onPay, onChanged }) {
  const [refusing, setRefusing] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (path, body, ok) => {
    setBusy(true);
    try { await api.post(`/projects/${projectId}/cash-requests/${path}`, body); toast.success(ok); setRefusing(null); setNote(""); onChanged?.(); } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  if (rows.length === 0) return null;
  const shown = rows.filter((r) => r.status === "REQUESTED" || rows.indexOf(r) < 6);
  return (
    <div className="rounded-xl border border-border bg-card" data-testid="cash-requests-panel">
      <p className="px-4 pt-3 text-sm font-medium">Cash requests ({rows.filter((r) => r.status === "REQUESTED").length} waiting)</p>
      <Table>
        <TableBody>
          {shown.map((r) => (
            <TableRow key={r.id} data-testid={`cash-request-${r.id}`}>
              <TableCell className="text-sm">{r.label}<div className="text-xs text-muted-foreground">{r.requested_by || "—"} · {fmtDate(r.created_at)}</div></TableCell>
              <TableCell className="text-right font-mono font-semibold">{inr(r.amount)}<div className="text-[11px] font-normal text-muted-foreground">had {inr(r.balance_at_request)}</div></TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.reason || "—"}{r.decision_note && <div>Refused: {r.decision_note}</div>}{r.decided_by && r.status !== "REQUESTED" && r.status !== "CANCELLED" && <div>By {r.decided_by}</div>}</TableCell>
              <TableCell><span className={`rounded px-2 py-0.5 text-[11px] font-medium ${REQUEST_TONE[r.status]}`} data-testid={`cash-request-status-${r.id}`}>{r.status === "REQUESTED" ? "Waiting" : r.status === "PAID" ? "Paid" : r.status === "REJECTED" ? "Refused" : "Withdrawn"}</span></TableCell>
              <TableCell className="text-right">
                {r.status === "REQUESTED" && canManage && (
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setRefusing(r)} data-testid={`cash-refuse-${r.id}`}>Refuse</Button>
                    <Button size="sm" onClick={() => onPay(r)} data-testid={`cash-pay-${r.id}`}>Pay</Button>
                  </div>
                )}
                {r.status === "REQUESTED" && r.mine && <Button size="sm" variant="ghost" disabled={busy} onClick={() => act(`${r.id}/cancel`, undefined, "Request withdrawn")} data-testid={`cash-withdraw-${r.id}`}>Withdraw</Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {refusing && (
        <Dialog open onOpenChange={(o) => !o && setRefusing(null)}>
          <DialogContent className="sm:max-w-md" data-testid="cash-refuse-dialog">
            <DialogHeader><DialogTitle>Refuse the request for {inr(refusing.amount)}?</DialogTitle><DialogDescription>Say why, so the site knows what to do next.</DialogDescription></DialogHeader>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="cash-refuse-note" />
            <DialogFooter><Button variant="outline" onClick={() => setRefusing(null)}>Cancel</Button>
              <Button disabled={busy || note.trim().length < 5} onClick={() => act(`${refusing.id}/reject`, { note: note.trim() }, "Request refused")} data-testid="cash-refuse-submit">Refuse</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function PettyCashDialog({ projectId, companyId, balance, onSaved, payRequest, onPayClose }) {
  const EMPTY = { entry_date: today(), kind: "TOPUP", amount: "", holder: "", bank_account_id: "", reference: "", remarks: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm(EMPTY); });      // closed without saving: opens fresh next time
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  useEffect(() => { if (payRequest) { setForm({ ...EMPTY, kind: "TOPUP", amount: String(payRequest.amount) }); setOpen(true); } }, [payRequest]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeAll = (o) => { setOpen(o); if (!o && payRequest) onPayClose?.(); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/petty-cash`, { ...form, cash_request_id: payRequest && form.kind === "TOPUP" ? payRequest.id : undefined, holder: form.holder || null, bank_account_id: form.bank_account_id || null, reference: form.reference || null, remarks: form.remarks || null });
      toast.success(form.kind === "TOPUP" ? "Petty cash given to the site" : "Petty cash returned to the bank");
      setForm(EMPTY);
      setOpen(false);
      if (payRequest) onPayClose?.();
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={closeAll}>
      <DialogTrigger asChild><Button variant="outline" data-testid="petty-cash-button"><Wallet className="h-4 w-4" /> Petty cash</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Petty cash — balance {inr(balance)}</DialogTitle>
          <DialogDescription>Give money to the site in-charge from the bank, or record unused cash coming back.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="TOPUP">Give to site (top-up)</SelectItem><SelectItem value="RETURN">Returned to office</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
            <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="petty-amount" /></div>
            <div><Label>Given to (site in-charge)</Label><Input value={form.holder} onChange={set("holder")} className="mt-1.5" /></div>
          </div>
          <BankAccountSelect companyId={companyId} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label={form.kind === "TOPUP" ? "Given from (bank / cash account)" : "Returned to (bank / cash account)"} testId="petty-bank-select" />
          <div><Label>UTR / cheque no.</Label><Input value={form.reference} onChange={set("reference")} className="mt-1.5" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FuelAccountDialog({ projectId, companyId, balance, onSaved, payRequest, onPayClose }) {
  const EMPTY = { entry_date: today(), kind: "TOPUP", amount: "", fuel_station: "", bank_account_id: "", reference: "", remarks: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm(EMPTY); });      // closed without saving: opens fresh next time
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  useEffect(() => { if (payRequest) { setForm({ ...EMPTY, kind: "TOPUP", amount: String(payRequest.amount) }); setOpen(true); } }, [payRequest]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeAll = (o) => { setOpen(o); if (!o && payRequest) onPayClose?.(); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/fuel-account`, { ...form, cash_request_id: payRequest && form.kind === "TOPUP" ? payRequest.id : undefined, fuel_station: form.fuel_station || null, bank_account_id: form.bank_account_id || null, reference: form.reference || null, remarks: form.remarks || null });
      toast.success(form.kind === "TOPUP" ? "Fuel Account topped up" : "Fuel Account refund recorded");
      setForm(EMPTY);
      setOpen(false);
      if (payRequest) onPayClose?.();
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={closeAll}>
      <DialogTrigger asChild><Button variant="outline" data-testid="fuel-account-button"><Fuel className="h-4 w-4" /> Fuel Account</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Fuel Account — balance {inr(balance)}</DialogTitle>
          <DialogDescription>Record paying the fuel station in advance, or unused fuel credit coming back.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="TOPUP">Pay fuel station (top-up)</SelectItem><SelectItem value="RETURN">Refund to office</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
            <div><Label>Amount (₹)</Label><Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="fuel-account-amount" /></div>
            <div><Label>Fuel station</Label><Input value={form.fuel_station} onChange={set("fuel_station")} className="mt-1.5" /></div>
          </div>
          <BankAccountSelect companyId={companyId} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label={form.kind === "TOPUP" ? "Paid from (bank / cash account)" : "Refunded to (bank / cash account)"} testId="fuel-bank-select" />
          <div><Label>Reference no.</Label><Input value={form.reference} onChange={set("reference")} className="mt-1.5" /></div>
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="fuel-account-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
